import { useEffect, useRef, useCallback } from "react";
import { usePlayerStore } from "@/lib/player/player.store";
import {
	PodcastPlaybackProgressService,
	type ListenedSegment,
} from "@/lib/podcast/podcastPlaybackProgress.service";
import { useSegmentTracking } from "./useSegmentTracking.hook";

/**
 * usePlaybackProgressSync Hook
 *
 * Automatically syncs podcast playback progress with the backend.
 * Tracks current playback position and periodically updates the server
 * so users can resume listening from where they left off.
 *
 * Features:
 * - Updates progress every 15 seconds while playing
 * - Saves immediately when pausing
 * - Saves on unmount (closing tab, navigating away)
 * - Auto-marks as completed at 95%
 * - Prevents timestamp mixing when switching podcasts
 * - Tracks which segments have been actually listened to
 */
export function usePlaybackProgressSync() {
	const podcastId = usePlayerStore((state) => state.podcastId);
	const isPlaying = usePlayerStore((state) => state.isPlaying);

	// Track listened segments
	const listenedSegments = useSegmentTracking();

	// Store latest segments in a ref to avoid causing useEffect re-runs
	const listenedSegmentsRef = useRef<ListenedSegment[]>([]);

	// Update ref whenever segments change (without causing re-renders)
	useEffect(() => {
		listenedSegmentsRef.current = listenedSegments;
	}, [listenedSegments]);

	// Track last update per podcast to prevent duplicate API calls
	const lastUpdateByPodcastRef = useRef<
		Map<string, { time: number; timestamp: number }>
	>(new Map());

	// Track completed podcasts to mark only once
	const markedAsCompletedRef = useRef<Set<string>>(new Set());

	// Track previous playing state to detect pause transitions
	const prevIsPlayingRef = useRef<boolean>(false);

	/**
	 * Send progress update to backend for a specific podcast.
	 * Uses getState() to always read fresh values and verify correct podcast.
	 */
	const updateProgress = useCallback(
		async (targetPodcastId: string, force = false) => {
			const state = usePlayerStore.getState();
			const { podcastId: currentPodcastId, currentTime, duration } = state;

			// Verify we're still on the same podcast
			if (targetPodcastId !== currentPodcastId) {
				return;
			}

			// Validate values - require at least 1 second to avoid capturing initial load states
			if (
				!Number.isFinite(duration) ||
				duration <= 0 ||
				!Number.isFinite(currentTime) ||
				currentTime < 1
			) {
				return;
			}

			// Sanity check: currentTime should never exceed duration
			// This catches cases where state from different podcasts got mixed
			if (currentTime > duration) {
				console.warn(
					`Progress sync: currentTime (${currentTime}s) exceeds duration (${duration}s) for podcast ${targetPodcastId}. Skipping save.`,
				);
				return;
			}

			// Skip if recently updated (unless forced)
			if (!force) {
				const lastUpdate = lastUpdateByPodcastRef.current.get(targetPodcastId);
				if (lastUpdate) {
					const timeSinceLastUpdate = Date.now() - lastUpdate.timestamp;
					const timeDifference = Math.abs(currentTime - lastUpdate.time);

					if (timeSinceLastUpdate < 5000 && timeDifference < 2) {
						return;
					}
				}
			}

			try {
				const shouldComplete =
					PodcastPlaybackProgressService.shouldMarkAsCompleted(
						currentTime,
						duration,
					);

				// Mark as completed only once
				if (
					shouldComplete &&
					!markedAsCompletedRef.current.has(targetPodcastId)
				) {
					markedAsCompletedRef.current.add(targetPodcastId);
				}

				const currentSegments = listenedSegmentsRef.current;
				console.debug(
					`[Progress Sync] Saving progress for podcast ${targetPodcastId.substring(0, 8)}...: ${Math.floor(currentTime)}s / ${Math.floor(duration)}s (${Math.floor((currentTime / duration) * 100)}%), segments: ${currentSegments.length}`,
				);

				await PodcastPlaybackProgressService.updateProgress(targetPodcastId, {
					currentTime,
					duration,
					completed: shouldComplete,
					listenedSegments:
						currentSegments.length > 0 ? currentSegments : undefined,
				});

				lastUpdateByPodcastRef.current.set(targetPodcastId, {
					time: currentTime,
					timestamp: Date.now(),
				});
			} catch (error) {
				console.error("Failed to update playback progress:", error);
			}
		},
		[],
	);

	/**
	 * Periodic updates while playing (every 15 seconds)
	 */
	useEffect(() => {
		if (!isPlaying || !podcastId) return;

		// Update immediately when playback starts
		updateProgress(podcastId);

		// Set up periodic updates
		const intervalId = setInterval(() => {
			updateProgress(podcastId);
		}, 15000);

		return () => clearInterval(intervalId);
		// Note: listenedSegments is intentionally NOT in deps - it's captured in updateProgress closure
		// and changes constantly, which would cause this effect to re-run constantly
	}, [isPlaying, podcastId, updateProgress]);

	/**
	 * Save immediately when pausing.
	 * Captures position synchronously before any async operations.
	 */
	useEffect(() => {
		// Check if just paused (transition from playing to paused)
		if (prevIsPlayingRef.current && !isPlaying && podcastId) {
			const state = usePlayerStore.getState();
			const { currentTime, duration } = state;

			// Update local tracking immediately (before async backend call)
			// Require at least 1 second to avoid capturing initial states
			if (
				Number.isFinite(currentTime) &&
				currentTime > 1 &&
				Number.isFinite(duration) &&
				duration > 0
			) {
				lastUpdateByPodcastRef.current.set(podcastId, {
					time: currentTime,
					timestamp: Date.now(),
				});

				// Then trigger backend update
				updateProgress(podcastId, true);
			}
		}

		prevIsPlayingRef.current = isPlaying;
	}, [isPlaying, podcastId, updateProgress]);

	/**
	 * Save progress when switching podcasts or unmounting.
	 * Captures snapshot at mount to prevent saving wrong timestamp to wrong podcast.
	 */
	useEffect(() => {
		const currentPodcastId = podcastId;

		// Capture current state at mount for this podcast
		const mountState = usePlayerStore.getState();
		const mountDuration = mountState.duration;

		return () => {
			if (!currentPodcastId) return;

			const state = usePlayerStore.getState();
			const { currentTime, duration, podcastId: activePodcastId } = state;

			// Check if podcast changed during cleanup
			if (activePodcastId !== currentPodcastId) {
				// Use last known position for the old podcast
				const lastKnown = lastUpdateByPodcastRef.current.get(currentPodcastId);
				if (lastKnown && lastKnown.time > 1) {
					// Use the last known duration if available, otherwise use mount duration
					const savedDuration =
						lastKnown.time > 0 ? mountDuration || duration : duration;
					if (savedDuration > 0) {
						PodcastPlaybackProgressService.updateProgress(currentPodcastId, {
							currentTime: lastKnown.time,
							duration: savedDuration,
							completed: PodcastPlaybackProgressService.shouldMarkAsCompleted(
								lastKnown.time,
								savedDuration,
							),
							listenedSegments:
								listenedSegmentsRef.current.length > 0
									? listenedSegmentsRef.current
									: undefined,
						}).catch(() => {
							// Silently fail on cleanup
						});
					}
				}
				return;
			}

			// Still on same podcast, save current position
			// Verify the state makes sense (currentTime should be valid for this duration)
			if (
				Number.isFinite(currentTime) &&
				currentTime > 1 &&
				Number.isFinite(duration) &&
				duration > 0 &&
				currentTime <= duration
			) {
				PodcastPlaybackProgressService.updateProgress(currentPodcastId, {
					currentTime,
					duration,
					completed: PodcastPlaybackProgressService.shouldMarkAsCompleted(
						currentTime,
						duration,
					),
					listenedSegments:
						listenedSegmentsRef.current.length > 0
							? listenedSegmentsRef.current
							: undefined,
				}).catch(() => {
					// Silently fail on cleanup
				});
			}
		};
		// Note: listenedSegments is intentionally NOT in deps - we use ref to access latest value
	}, [podcastId]);
}
